import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { Github, Linkedin, Facebook, Instagram } from 'lucide-react';
import './Hero.css';

const Hero = () => {
  const { theme } = useTheme();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section id="home" className={`hero ${theme}`}>
      {/* Enhanced Background with Animations */}
      <div className="hero-background">
        {/* Primary Background Gradient */}
        <div className="background-gradient" />
        
        {/* Interactive Orb 1 */}
        <div 
          className="floating-orb orb-1"
          style={{
            left: `${20 + mousePosition.x * 0.02}%`,
            top: `${10 + mousePosition.y * 0.03}%`,
          }}
        />
        
        {/* Interactive Orb 2 */}
        <div 
          className="floating-orb orb-2"
          style={{
            right: `${15 + mousePosition.x * 0.01}%`,
            bottom: `${20 + mousePosition.y * 0.02}%`,
          }}
        />
        
        {/* Mesh Gradient Overlay */}
        <div className="mesh-gradient" />

        {/* Animated Grid Pattern */}
        <div className="grid-pattern">
          <svg className="grid-svg" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="currentColor" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Floating Particles */}
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="floating-particle"
            style={{
              left: `${15 + i * 12}%`,
              top: `${20 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.3}s`,
              animationDuration: `${3 + i * 0.5}s`
            }}
          />
        ))}
      </div>

      {/* Main Content - Preserving Original Structure */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="hero-content"
      >
        {/* Greeting Badge */}
        <div className={`greeting-badge ${isLoaded ? 'loaded' : ''}`}>
          <span className="wave-emoji">👋</span>
          Welcome to my digital space
        </div>

        {/* Enhanced H1 with Original Logic */}
        <h1 className={`hero-title ${isLoaded ? 'loaded' : ''}`}>
          Hi, I'm{' '}
          <span className="name-highlight">
            <span className="gradient-text">
              Chathushki Kumarage
            </span>
            <div className="name-underline" />
            <div className="name-glow" />
          </span>
        </h1>

        {/* Enhanced H2 with Original Logic */}
        <h2 className={`hero-subtitle ${isLoaded ? 'loaded' : ''}`}>
          <span className="subtitle-content">
            Software Engineer
            <span className="separator">•</span>
            UI/UX Designer
            <span className="typing-cursor">|</span>
          </span>
        </h2>

        {/* Enhanced P with Original Logic */}
        <p className={`hero-description ${isLoaded ? 'loaded' : ''}`}>
          I build exceptional digital experiences that blend creativity with functionality
        </p>

        {/* Enhanced Button with Original Logic */}
        <div className={`cta-container ${isLoaded ? 'loaded' : ''}`}>
          <button className="cta-button">
            <span className="button-content">
              View My Work
              <svg 
                className="button-arrow" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </span>
            <div className="button-bg-hover" />
            <div className="button-shine" />
          </button>
        </div>

        {/* Social Links */}
<div className={`social-links ${isLoaded ? 'loaded' : ''}`}>
  {[
    { platform: 'GitHub', url: 'https://github.com/chathushkiupulini', icon: <Github size={20}/> },
    { platform: 'LinkedIn', url: 'https://www.linkedin.com/in/chathushki-kumarage/', icon: <Linkedin size={20} /> },
    { platform: 'Facebook', url: 'https://www.facebook.com/share/1D86szJ93v/', icon:<Facebook size={20} /> },
    { platform: 'Instagram', url: 'https://www.instagram.com/chathushki.asmada/?hl=en', icon:  <Instagram size={20} /> }
  ].map((social) => (
    <a
      key={social.platform}
      href={social.url}
      className="social-link"
      target="_blank"
      rel="noopener noreferrer"
    >
      {social.icon}
    </a>
  ))}
</div>
      </motion.div>

      {/* Scroll Indicator */}
      <div className={`scroll-indicator ${isLoaded ? 'loaded' : ''}`}>
        <div className="scroll-content">
          <span className="scroll-text">Scroll to explore</span>
          <div className="scroll-mouse">
            <div className="scroll-wheel" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;